import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { AppCard } from '../../components/ui/AppCard';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { useStore } from '../../store/StoreContext';
import { SocialLoginButtons } from './SocialLoginButtons';
import { PasskeyButton } from './PasskeyButton';
import { useFeature } from '../../lib/features';
import { TurnstileWidget, type TurnstileStatus } from './TurnstileWidget';

const EMAIL_RE = /\S+@\S+\.\S+/;
// Mirrors TurnstileWidget's own site-key check - when it's configured, the
// backend's VerifyTurnstile is set up to fail closed on an empty token
// (see that component's comment), so "Send code" shouldn't even be
// reachable until a token - or a clear "blocked" explanation - exists.
const turnstileRequired = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function RegisterScreen() {
  const { register, registerWithPasskey, sendRegisterCode, resendRegisterCode } = useStore();
  const socialOn = useFeature('auth.social_login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>('loading');

  const emailValid = EMAIL_RE.test(email.trim());
  const turnstileBlocking = turnstileRequired && turnstileStatus !== 'ready';

  const requestCode = async () => {
    if (!emailValid) return setError('Enter a valid email address first.');
    if (turnstileBlocking) {
      return setError(
        turnstileStatus === 'blocked'
          ? 'Verification failed to load - use the Retry link below the password field.'
          : 'Still loading the verification check, one moment...',
      );
    }
    setSendingCode(true);
    setError('');
    try {
      const id = await (challengeId
        ? resendRegisterCode(challengeId).then(() => challengeId)
        : sendRegisterCode(email.trim(), turnstileToken));
      setChallengeId(id);
      setNotice(`We sent a 6-digit code to ${email.trim()}. Enter it below to finish signing up.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a verification code.');
    } finally {
      setSendingCode(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name can't be empty.");
    if (!emailValid) return setError("Email can't be empty.");
    if (!challengeId) return setError('Tap "Send code" and enter the code we email you.');
    if (!code.trim()) return setError('Enter the verification code from your email.');
    if (password.trim().length < 5) return setError('Password must be at least 5 characters long.');
    setLoading(true);
    try {
      await register(name.trim(), email.trim(), password, challengeId, code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create an account.');
    } finally {
      setLoading(false);
    }
  };

  // Same checks as submit() minus the password: the passkey replaces it.
  const submitPasskey = async () => {
    if (!name.trim()) return setError("Name can't be empty.");
    if (!emailValid) return setError("Email can't be empty.");
    if (!challengeId) return setError('Tap "Send code" and enter the code we email you.');
    if (!code.trim()) return setError('Enter the verification code from your email.');
    setPasskeyLoading(true);
    try {
      await registerWithPasskey(name.trim(), email.trim(), challengeId, code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create an account.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <AuthLayout>
      <SectionTitle title="Create Account" />
      <AppCard>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <TextField label="Name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />

          <div className="flex flex-col gap-1.5">
            <div className="flex items-end gap-2">
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // a changed address invalidates a code sent to the old one
                  if (challengeId) {
                    setChallengeId('');
                    setCode('');
                    setNotice('');
                  }
                }}
              />
              <Button
                type="button"
                variant="bordered"
                loading={sendingCode}
                disabled={sendingCode || !emailValid || turnstileBlocking}
                onClick={requestCode}
                className="shrink-0 whitespace-nowrap"
              >
                {challengeId ? 'Resend' : 'Send code'}
              </Button>
            </div>
            {notice && <span className="text-xs text-black/55 dark:text-white/55">{notice}</span>}
          </div>

          <TextField
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={challengeId ? '6-digit code' : 'Tap "Send code" first'}
            disabled={!challengeId}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />

          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <TurnstileWidget onToken={setTurnstileToken} onStatusChange={setTurnstileStatus} />

          <Button type="submit" full loading={loading} disabled={loading || !challengeId || !code.trim()}>
            Sign Up
          </Button>
          <PasskeyButton onClick={submitPasskey} loading={passkeyLoading} disabled={loading || !challengeId || !code.trim()}>
            Sign up with a passkey instead
          </PasskeyButton>
          <div className="text-sm text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
              Login
            </Link>
          </div>
        </form>
      </AppCard>
      {socialOn && (
        <AppCard>
          <SocialLoginButtons />
        </AppCard>
      )}
      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </AuthLayout>
  );
}
